import { useRef, useState } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ImagePlus, Copy, Trash2, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, copyImage, downloadAll } from './flyerUtils';

export function FlyerPhotos({ flyer, ensureSaved, onChange, address }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const photos = flyer?.photos || [];

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const f = await ensureSaved();
      let list = [...(f.photos || [])];
      for (const file of files) {
        const small = await compressImage(file);
        const fd = new FormData(); fd.append('file', small);
        try { const r = await API.post(`/flyers/${f.id}/photos`, fd); list = [...list, r.data]; onChange(list); }
        catch (e) { toast.error(e.response?.data?.detail || `Couldn't upload ${file.name}`); }
      }
    } finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  const remove = async (pid) => {
    try { await API.delete(`/flyers/${flyer.id}/photos/${pid}`); onChange(photos.filter(p => p.id !== pid)); } catch { toast.error('Could not remove photo'); }
  };
  const copy = async (pid) => {
    try { await copyImage(`/api/fp/${pid}`); toast.success('Photo copied — paste it into your text'); }
    catch { toast.error('Your browser blocked image copy — use Download instead'); }
  };

  return (
    <div className="space-y-2" data-testid="flyer-photos">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium text-gray-600">Photos ({photos.length}/20)</p>
        <div className="flex gap-2">
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={e => upload([...e.target.files])} data-testid="flyer-photo-input" />
          <Button size="sm" variant="outline" className="h-8" onClick={() => input.current?.click()} disabled={busy || photos.length >= 20} data-testid="flyer-add-photos-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5 mr-1" />}Add Photos
          </Button>
          <Button size="sm" variant="outline" className="h-8" disabled={!photos.length} onClick={() => downloadAll(photos, address).catch(() => toast.error('Download failed'))} data-testid="flyer-download-all-btn"><Download className="h-3.5 w-3.5 mr-1" />Download All</Button>
        </div>
      </div>
      {photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map(p => (
            <div key={p.id} className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-gray-200" data-testid={`flyer-photo-${p.id}`}>
              <img src={`/api/fp/${p.id}`} alt="Property" className="h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 bg-gradient-to-t from-black/70 to-transparent p-1 opacity-100 sm:opacity-0 transition-opacity group-hover:opacity-100">
                <button onClick={() => copy(p.id)} className="rounded bg-black/60 p-1 text-white hover:bg-black" title="Copy photo" data-testid={`flyer-copy-photo-${p.id}`}><Copy className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(p.id)} className="rounded bg-black/60 p-1 text-white hover:bg-red-600" title="Remove" data-testid={`flyer-delete-photo-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : <button onClick={() => input.current?.click()} className="w-full rounded-lg border border-dashed border-gray-300 py-6 text-xs text-gray-500 hover:border-[#0073ea]" data-testid="flyer-photo-dropzone">Click to add property photos</button>}
    </div>
  );
}
